import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { generateExcelFileName } from "../../../../utils/excelUtils";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = String(text).length * charWidth + padding;
  return Math.min(estimatedWidth, (8 * window.innerWidth) / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Sub-header definitions for observations and module inspections
const observationSubHeaders = [
  { key: "obs1", label: "Obs 1" },
  { key: "obs2", label: "Obs 2" },
  { key: "remarks", label: "Remarks" },
];

const moduleSubHeaders = [
  { key: "moduleInspection", label: "Module Sr. No" },
  { key: "moduleStatus", label: "Status" },
  { key: "remarks", label: "Remarks" },
];

const PrePostLaminationTable = ({
  formName = "Lamination",
  subForm = [],
  data = [],
  subFormMappings = {},
  startDate,
  endDate,
  shifts = [],
}) => {
  // Static headers for Observations Table
  const observationStaticHeaders = [
    { id: "subForm", label: "Sub-form" },
    { id: "tableName", label: "Table Name" },
    { id: "field", label: "Product" },
    { id: "specification", label: "Specification" },
    { id: "frequency", label: "Frequency" },
  ];

  // Reverse mapping for API subform names to UI names
  const reverseSubFormMappings = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(subFormMappings || {}).map(([uiName, apiName]) => [apiName, uiName])
      ),
    [subFormMappings]
  );

  // Split data into observation and module data
  const observationData = useMemo(
    () => (data || []).filter((item) => item.tableName !== "ModuleInspection"),
    [data]
  );
  const moduleData = useMemo(
    () => (data || []).filter((item) => item.tableName === "ModuleInspection"),
    [data]
  );

  // Get all dates strictly in the range
  const dates = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  // Use provided shifts, fallback to data, or default
  const effectiveShifts = useMemo(() => {
    if (shifts && shifts.length > 0) return shifts;
    const found = [...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))].sort();
    return found.length > 0 ? found : ["Morning", "Afternoon", "Night"];
  }, [shifts, data]);

  // Hierarchical header tree for Observations: Date -> Shifts -> 3 Metrics
  const observationHeaderStructure = useMemo(() => {
    return dates.map((date) => {
      const shiftNodes = effectiveShifts.map((shift) => {
        const key = `${date}_${shift}`;
        const metrics = observationSubHeaders.map((m) => ({
          id: `${m.key}_${key}`,
          metricKey: m.key,
          label: m.label,
          date,
          shift,
        }));

        return {
          shift,
          key,
          colSpan: observationSubHeaders.length,
          metrics,
        };
      });

      return {
        date,
        colSpan: effectiveShifts.length * observationSubHeaders.length,
        shifts: shiftNodes,
      };
    });
  }, [dates, effectiveShifts]);

  // Flattened leaf columns for Observations
  const flatObservationColumns = useMemo(() => {
    return observationHeaderStructure.flatMap((d) => d.shifts.flatMap((s) => s.metrics));
  }, [observationHeaderStructure]);

  // Hierarchical header tree for Module Inspection: Date -> Shifts -> 3 Metrics
  const moduleHeaderStructure = useMemo(() => {
    return dates.map((date) => {
      const shiftNodes = effectiveShifts.map((shift) => {
        const key = `${date}_${shift}`;
        const metrics = moduleSubHeaders.map((m) => ({
          id: `${m.key}_${key}`,
          metricKey: m.key,
          label: m.label,
          date,
          shift,
        }));

        return {
          shift,
          key,
          colSpan: moduleSubHeaders.length,
          metrics,
        };
      });

      return {
        date,
        colSpan: effectiveShifts.length * moduleSubHeaders.length,
        shifts: shiftNodes,
      };
    });
  }, [dates, effectiveShifts]);

  // Flattened leaf columns for Module Inspection
  const flatModuleColumns = useMemo(() => {
    return moduleHeaderStructure.flatMap((d) => d.shifts.flatMap((s) => s.metrics));
  }, [moduleHeaderStructure]);

  // Column widths definition
  const observationColumnWidths = useMemo(() => {
    const widths = {
      subForm: estimateTextWidth("Sub-form"),
      tableName: estimateTextWidth("Table Name"),
      field: estimateTextWidth("Product"),
      specification: estimateTextWidth("Specification"),
      frequency: estimateTextWidth("Frequency"),
      obs1: 100,
      obs2: 100,
      remarks: 140,
    };

    observationData.forEach((item) => {
      if (item?.subForm) {
        const uiSubForm = reverseSubFormMappings[item.subForm] || item.subForm;
        widths.subForm = Math.min(Math.max(widths.subForm, estimateTextWidth(uiSubForm)), (8 * window.innerWidth) / 100);
      }
      if (item?.tableName) {
        widths.tableName = Math.min(Math.max(widths.tableName, estimateTextWidth(item.tableName)), (8 * window.innerWidth) / 100);
      }
      if (item?.field) {
        widths.field = Math.min(Math.max(widths.field, estimateTextWidth(item.field)), (8 * window.innerWidth) / 100);
      }
      if (item?.specification) {
        widths.specification = Math.min(Math.max(widths.specification, estimateTextWidth(item.specification)), (8 * window.innerWidth) / 100);
      }
      if (item?.frequency) {
        widths.frequency = Math.min(Math.max(widths.frequency, estimateTextWidth(item.frequency)), (8 * window.innerWidth) / 100);
      }
    });

    return widths;
  }, [observationData, reverseSubFormMappings]);

  const moduleColumnWidths = useMemo(() => {
    return {
      moduleInspection: 140,
      moduleStatus: 120,
      remarks: 150,
    };
  }, []);

  const getObservationLeftPosition = (index) => {
    const staticKeys = ["subForm", "tableName", "field", "specification", "frequency"];
    return staticKeys.slice(0, index).reduce((sum, key) => sum + (observationColumnWidths[key] || 100), 0);
  };

  // Group Observation Data
  const observationGroupedData = useMemo(() => {
    return observationData.reduce((acc, item) => {
      if (!item.subForm || !item.tableName || !item.field) return acc;
      const uiSubform = reverseSubFormMappings[item.subForm] || item.subForm;
      const field = item.field || "N/A";
      const key = `${uiSubform}:${item.tableName}:${field}`;

      if (!acc[key]) {
        acc[key] = {
          subForm: uiSubform,
          tableName: item.tableName,
          field,
          specification: item.specification || "N/A",
          frequency: item.frequency || "N/A",
          observations: {},
        };
      }

      const obsKey = `${formatDate(item.inspectionDate)}_${item.inspectionShift}`;
      acc[key].observations[obsKey] = {
        obs1: item.obs1 || "N/A",
        obs2: item.obs2 || "N/A",
        remarks: item.remarks || "N/A",
      };
      return acc;
    }, {});
  }, [observationData, reverseSubFormMappings]);

  // Group Module Inspection Data
  const moduleGroupedData = useMemo(() => {
    return moduleData.reduce((acc, item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) return acc;
      const key = `${date}_${shift}`;

      if (!acc[key]) {
        acc[key] = {
          date,
          shift,
          modules: [],
        };
      }
      acc[key].modules.push({
        moduleInspection: item.moduleInspection || "N/A",
        moduleStatus: item.moduleStatus || "N/A",
        remarks: item.remarks || "",
      });
      return acc;
    }, {});
  }, [moduleData]);

  // Filter observations by subForm
  const filteredObservationData = useMemo(() => {
    return Object.values(observationGroupedData).filter(
      (item) => subForm.length === 0 || subForm.includes(item.subForm)
    );
  }, [observationGroupedData, subForm]);

  const isVisualInspection =
    formName === "Post-Lamination" && subForm.includes("Visual Inspection (Post)");

  const colsPerShift = 3;
  const colsPerDate = effectiveShifts.length * colsPerShift;

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // === SHEET 1: Observations Table ===
    const staticCols = ["Sub-form", "Table Name", "Product", "Specification", "Frequency"];
    const obsRow1 = [...staticCols];
    const obsRow2 = ["", "", "", "", ""];
    const obsRow3 = ["", "", "", "", ""];
    const merges1 = [];

    staticCols.forEach((_, idx) => {
      merges1.push({ s: { r: 0, c: idx }, e: { r: 2, c: idx } });
    });

    let colIdx = staticCols.length;

    observationHeaderStructure.forEach((dateNode) => {
      obsRow1.push(dateNode.date);
      for (let i = 1; i < colsPerDate; i++) obsRow1.push("");
      if (colsPerDate > 1) {
        merges1.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + colsPerDate - 1 } });
      }

      dateNode.shifts.forEach((shiftNode) => {
        obsRow2.push(`Shift ${shiftNode.shift}`);
        for (let i = 1; i < colsPerShift; i++) obsRow2.push("");
        if (colsPerShift > 1) {
          merges1.push({ s: { r: 1, c: colIdx }, e: { r: 1, c: colIdx + colsPerShift - 1 } });
        }

        shiftNode.metrics.forEach((m) => {
          obsRow3.push(m.label);
          colIdx += 1;
        });
      });
    });

    const obsDataRows = filteredObservationData.map((row) => {
      const r = [row.subForm, row.tableName, row.field, row.specification, row.frequency];
      flatObservationColumns.forEach((col) => {
        const obs = row.observations[`${col.date}_${col.shift}`] || {};
        r.push(obs[col.metricKey] || "N/A");
      });
      return r;
    });

    const obsSheetData = [obsRow1, obsRow2, obsRow3, ...obsDataRows];
    const ws1 = XLSX.utils.aoa_to_sheet(obsSheetData);
    if (merges1.length > 0) ws1["!merges"] = merges1;
    ws1["!cols"] = [
      { wch: 20 },
      { wch: 18 },
      { wch: 25 },
      { wch: 30 },
      { wch: 15 },
      ...Array(flatObservationColumns.length).fill({ wch: 16 }),
    ];
    XLSX.utils.book_append_sheet(wb, ws1, "Observations");

    // === SHEET 2: Module Inspection Table ===
    if (isVisualInspection) {
      const modRow1 = [];
      const modRow2 = [];
      const modRow3 = [];
      const merges2 = [];
      let modColIdx = 0;

      moduleHeaderStructure.forEach((dateNode) => {
        modRow1.push(dateNode.date);
        for (let i = 1; i < colsPerDate; i++) modRow1.push("");
        if (colsPerDate > 1) {
          merges2.push({ s: { r: 0, c: modColIdx }, e: { r: 0, c: modColIdx + colsPerDate - 1 } });
        }

        dateNode.shifts.forEach((shiftNode) => {
          modRow2.push(`Shift ${shiftNode.shift}`);
          for (let i = 1; i < colsPerShift; i++) modRow2.push("");
          if (colsPerShift > 1) {
            merges2.push({ s: { r: 1, c: modColIdx }, e: { r: 1, c: modColIdx + colsPerShift - 1 } });
          }

          shiftNode.metrics.forEach((m) => {
            modRow3.push(m.label);
            modColIdx += 1;
          });
        });
      });

      const maxModules = Math.max(
        0,
        ...Object.values(moduleGroupedData).map((group) => group.modules.length)
      );

      const modDataRows = Array.from({ length: maxModules }).map((_, rowIndex) => {
        const r = [];
        moduleHeaderStructure.forEach((dateNode) => {
          dateNode.shifts.forEach((shiftNode) => {
            const group = moduleGroupedData[shiftNode.key] || { modules: [] };
            const mod = group.modules[rowIndex] || {};
            shiftNode.metrics.forEach((m) => {
              r.push(mod[m.metricKey] || "");
            });
          });
        });
        return r;
      });

      const modSheetData = [modRow1, modRow2, modRow3, ...modDataRows];
      const ws2 = XLSX.utils.aoa_to_sheet(modSheetData);
      if (merges2.length > 0) ws2["!merges"] = merges2;
      ws2["!cols"] = Array(flatModuleColumns.length).fill({ wch: 18 });
      XLSX.utils.book_append_sheet(wb, ws2, "Module Inspection");
    }

    const fileName = generateExcelFileName(formName, startDate, endDate);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  const hasData = filteredObservationData.length > 0 || Object.keys(moduleGroupedData).length > 0;

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        {formName} Inspection Data
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 3 }}
        disabled={!hasData || dates.length === 0}
      >
        Download as Excel
      </Button>

      {/* Observations Table */}
      <Typography variant="subtitle1" gutterBottom>
        Observations Table
      </Typography>
      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto", marginBottom: "20px" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            {/* Row 1: Static Headers + Dates */}
            <TableRow>
              {observationStaticHeaders.map((header, index) => (
                <StickyTableCell
                  key={header.id}
                  rowSpan={3}
                  columnWidth={observationColumnWidths[header.id]}
                  left={getObservationLeftPosition(index)}
                  isLastSticky={index === observationStaticHeaders.length - 1}
                >
                  {header.label}
                </StickyTableCell>
              ))}
              {observationHeaderStructure.map((dateNode) => (
                <CustomTableCell
                  key={dateNode.date}
                  colSpan={dateNode.colSpan}
                  columnWidth={colsPerShift * 120 * effectiveShifts.length}
                >
                  {dateNode.date}
                </CustomTableCell>
              ))}
            </TableRow>

            {/* Row 2: Shifts */}
            <TableRow>
              {observationHeaderStructure.flatMap((dateNode) =>
                dateNode.shifts.map((shiftNode) => (
                  <CustomTableCell
                    key={`${dateNode.date}_${shiftNode.shift}`}
                    colSpan={shiftNode.colSpan}
                    columnWidth={colsPerShift * 120}
                  >
                    Shift {shiftNode.shift}
                  </CustomTableCell>
                ))
              )}
            </TableRow>

            {/* Row 3: Sub-headers (Obs 1, Obs 2, Remarks) */}
            <TableRow>
              {observationHeaderStructure.flatMap((dateNode) =>
                dateNode.shifts.flatMap((shiftNode) =>
                  shiftNode.metrics.map((m) => (
                    <CustomTableCell
                      key={m.id}
                      columnWidth={observationColumnWidths[m.metricKey] || 120}
                    >
                      {m.label}
                    </CustomTableCell>
                  ))
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {dates.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={observationStaticHeaders.length}
                  align="center"
                  rowIndex={0}
                  columnWidth={observationColumnWidths.subForm}
                  left={getObservationLeftPosition(0)}
                  isLastSticky={false}
                >
                  No dates selected
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : filteredObservationData.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={observationStaticHeaders.length + flatObservationColumns.length}
                  align="center"
                  rowIndex={0}
                  columnWidth={observationColumnWidths.subForm}
                  left={getObservationLeftPosition(0)}
                  isLastSticky={false}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              filteredObservationData.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={observationColumnWidths.subForm}
                    left={getObservationLeftPosition(0)}
                    isLastSticky={false}
                  >
                    {row.subForm}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={observationColumnWidths.tableName}
                    left={getObservationLeftPosition(1)}
                    isLastSticky={false}
                  >
                    {row.tableName}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={observationColumnWidths.field}
                    left={getObservationLeftPosition(2)}
                    isLastSticky={false}
                  >
                    {row.field}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={observationColumnWidths.specification}
                    left={getObservationLeftPosition(3)}
                    isLastSticky={false}
                  >
                    {row.specification}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={observationColumnWidths.frequency}
                    left={getObservationLeftPosition(4)}
                    isLastSticky={true}
                  >
                    {row.frequency}
                  </StickyBodyTableCell>

                  {flatObservationColumns.map((col) => {
                    const obsData = row.observations[`${col.date}_${col.shift}`] || {};
                    return (
                      <BodyTableCell
                        key={`${col.id}_${index}`}
                        columnWidth={observationColumnWidths[col.metricKey] || 120}
                      >
                        {obsData[col.metricKey] || "N/A"}
                      </BodyTableCell>
                    );
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Module Inspection Table (rendered only if condition is met) */}
      {isVisualInspection && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Module Inspection Table
          </Typography>
          <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
            <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
              <TableHead>
                {/* Row 1: Dates */}
                <TableRow>
                  {moduleHeaderStructure.map((dateNode) => (
                    <CustomTableCell
                      key={dateNode.date}
                      colSpan={dateNode.colSpan}
                      columnWidth={colsPerShift * 140 * effectiveShifts.length}
                    >
                      {dateNode.date}
                    </CustomTableCell>
                  ))}
                </TableRow>

                {/* Row 2: Shifts */}
                <TableRow>
                  {moduleHeaderStructure.flatMap((dateNode) =>
                    dateNode.shifts.map((shiftNode) => (
                      <CustomTableCell
                        key={`${dateNode.date}_${shiftNode.shift}`}
                        colSpan={shiftNode.colSpan}
                        columnWidth={colsPerShift * 140}
                      >
                        Shift {shiftNode.shift}
                      </CustomTableCell>
                    ))
                  )}
                </TableRow>

                {/* Row 3: Sub-headers */}
                <TableRow>
                  {moduleHeaderStructure.flatMap((dateNode) =>
                    dateNode.shifts.flatMap((shiftNode) =>
                      shiftNode.metrics.map((m) => (
                        <CustomTableCell
                          key={m.id}
                          columnWidth={moduleColumnWidths[m.metricKey] || 140}
                        >
                          {m.label}
                        </CustomTableCell>
                      ))
                    )
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {Object.values(moduleGroupedData).length === 0 ? (
                  <CustomTableRow index={0}>
                    <BodyTableCell
                      colSpan={flatModuleColumns.length || 1}
                      align="center"
                      columnWidth={140}
                    >
                      No data available
                    </BodyTableCell>
                  </CustomTableRow>
                ) : (
                  (() => {
                    const maxModules = Math.max(
                      0,
                      ...Object.values(moduleGroupedData).map((group) => group.modules.length)
                    );

                    return Array.from({ length: maxModules }).map((_, rowIndex) => (
                      <CustomTableRow key={rowIndex} index={rowIndex}>
                        {moduleHeaderStructure.flatMap((dateNode) =>
                          dateNode.shifts.flatMap((shiftNode) => {
                            const group = moduleGroupedData[shiftNode.key] || { modules: [] };
                            const mod = group.modules[rowIndex] || {};
                            return shiftNode.metrics.map((m) => (
                              <BodyTableCell
                                key={`${m.id}_${rowIndex}`}
                                columnWidth={moduleColumnWidths[m.metricKey] || 140}
                              >
                                {mod[m.metricKey] || "N/A"}
                              </BodyTableCell>
                            ));
                          })
                        )}
                      </CustomTableRow>
                    ));
                  })()
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </div>
  );
};

export default PrePostLaminationTable;